import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { apiClient } from "../services/apiClient";

const ProposalContext = createContext();

export const useProposal = () => {
  const context = useContext(ProposalContext);
  if (!context) {
    throw new Error("useProposal must be used within a ProposalProvider");
  }
  return context;
};

export const ProposalProvider = ({ children }) => {
  const [proposals, setProposals] = useState([]);
  const [currentProposal, setCurrentProposal] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearError = useCallback(() => {
    setError("");
  }, []);

  // Fetch all proposals with optional filters
  const fetchProposals = useCallback(async (filters = {}) => {
    setLoading(true);
    setError("");

    try {
      const response = await apiClient.getProposals(filters);

      if (response.success) {
        setProposals(response.data.proposals || []);
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(response.message || "Failed to fetch proposals");
      }
    } catch (err) {
      console.error("Error fetching proposals:", err);
      setError(err.message);
      return {
        success: false,
        error: err.message,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // AUTO-FETCH PROPOSALS ON MOUNT - This is the key fix!
  useEffect(() => {
    console.log("ProposalContext: Auto-fetching proposals on mount");
    fetchProposals();
  }, [fetchProposals]);

  // Fetch a single proposal by ID
  const fetchProposal = useCallback(async (proposalId) => {
    setLoading(true);
    setError("");

    try {
      const response = await apiClient.getProposal(proposalId);

      if (response.success) {
        setCurrentProposal(response.data.proposal);
        return {
          success: true,
          data: response.data.proposal,
        };
      } else {
        throw new Error(response.message || "Failed to fetch proposal");
      }
    } catch (err) {
      console.error("Error fetching proposal:", err);
      setError(err.message);
      setCurrentProposal(null);
      return {
        success: false,
        error: err.message,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new proposal
  const createProposal = useCallback(
    async (proposalData) => {
      setLoading(true);
      setError("");

      try {
        const response = await apiClient.createProposal(proposalData);

        if (response.success) {
          // Refresh proposals list
          await fetchProposals();

          return {
            success: true,
            data: response.data,
          };
        } else {
          throw new Error(response.message || "Failed to create proposal");
        }
      } catch (err) {
        console.error("Error creating proposal:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [fetchProposals]
  );

  // Update an existing proposal
  const updateProposal = useCallback(
    async (proposalId, proposalData) => {
      setLoading(true);
      setError("");

      try {
        const response = await apiClient.updateProposal(
          proposalId,
          proposalData
        );

        if (response.success) {
          // Refresh current proposal if it's the one being updated
          if (currentProposal && currentProposal.proposal_id === proposalId) {
            await fetchProposal(proposalId);
          }

          // Refresh proposals list
          await fetchProposals();

          return {
            success: true,
            data: response.data,
          };
        } else {
          throw new Error(response.message || "Failed to update proposal");
        }
      } catch (err) {
        console.error("Error updating proposal:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [currentProposal, fetchProposal, fetchProposals]
  );

  // Delete a proposal
  const deleteProposal = useCallback(
    async (proposalId) => {
      setLoading(true);
      setError("");

      try {
        const response = await apiClient.deleteProposal(proposalId);

        if (response.success) {
          // Clear current proposal if it's the one being deleted
          if (currentProposal && currentProposal.proposal_id === proposalId) {
            setCurrentProposal(null);
          }

          // Refresh proposals list
          await fetchProposals();

          return {
            success: true,
            data: response.data,
          };
        } else {
          throw new Error(response.message || "Failed to delete proposal");
        }
      } catch (err) {
        console.error("Error deleting proposal:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [currentProposal, fetchProposals]
  );

  // Update proposal status only (lightweight update)
  const updateProposalStatus = useCallback(
    async (proposalId, status) => {
      try {
        const response = await apiClient.updateProposalStatus(proposalId, status);

        if (response.success) {
          // Update the proposal in the local state
          setProposals((prevProposals) =>
            prevProposals.map((p) =>
              p.proposal_id === proposalId ? { ...p, status } : p
            )
          );

          // Update current proposal if it's the one being updated
          if (currentProposal && currentProposal.proposal_id === proposalId) {
            setCurrentProposal((prev) => ({ ...prev, status }));
          }

          return {
            success: true,
            data: response.data,
          };
        } else {
          throw new Error(response.message || "Failed to update proposal status");
        }
      } catch (err) {
        console.error("Error updating proposal status:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    [currentProposal]
  );

  // Fetch customers for dropdown
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await apiClient.getCustomers();

      if (response.success) {
        setCustomers(response.data.customers || []);
        return {
          success: true,
          data: response.data.customers,
        };
      } else {
        throw new Error(response.message || "Failed to fetch customers");
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      // Don't set error state for customers fetch as it's not critical
      return {
        success: false,
        error: err.message,
      };
    }
  }, []);

  // Create a new customer
  const createCustomer = useCallback(
    async (customerData) => {
      try {
        const response = await apiClient.createCustomer(customerData);

        if (response.success) {
          // Refresh customers list
          await fetchCustomers();

          return {
            success: true,
            data: response.data,
          };
        } else {
          throw new Error(response.message || "Failed to create customer");
        }
      } catch (err) {
        console.error("Error creating customer:", err);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    [fetchCustomers]
  );

  // Search proposals
  const searchProposals = useCallback(
    async (searchTerm, filters = {}) => {
      return await fetchProposals({
        ...filters,
        customer: searchTerm,
      });
    },
    [fetchProposals]
  );

  // Get proposal statistics
  const getProposalStats = useCallback(() => {
    const stats = {
      total: proposals.length,
      active: proposals.filter((p) => p.status === "Active").length,
      completed: proposals.filter((p) => p.status === "Completed").length,
      archived: proposals.filter((p) => p.status === "Archived").length,
      rfps: proposals.filter((p) => p.proposal_type === "RFP").length,
      pastProposals: proposals.filter(
        (p) => p.proposal_type === "Past_Proposal"
      ).length,
      totalValue: proposals.reduce(
        (sum, p) => sum + (parseFloat(p.contract_value) || 0),
        0
      ),
      averageValue:
        proposals.length > 0
          ? proposals.reduce(
              (sum, p) => sum + (parseFloat(p.contract_value) || 0),
              0
            ) / proposals.length
          : 0,
    };

    return stats;
  }, [proposals]);

  // Clear current proposal
  const clearCurrentProposal = useCallback(() => {
    setCurrentProposal(null);
  }, []);

  const value = {
    // State
    proposals,
    currentProposal,
    customers,
    loading,
    error,

    // Actions
    fetchProposals,
    fetchProposal,
    createProposal,
    updateProposal,
    updateProposalStatus,
    deleteProposal,
    fetchCustomers,
    createCustomer,
    searchProposals,
    clearCurrentProposal,
    clearError,

    // Computed
    getProposalStats,
  };

  return (
    <ProposalContext.Provider value={value}>
      {children}
    </ProposalContext.Provider>
  );
};
